import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './Offers.css';

const API_URL  = process.env.REACT_APP_API_URL;
const ECOM_URL = process.env.REACT_APP_ECOM_URL || 'https://prebook.thenewluxury.in';

const encodeRef = (payload) =>
  btoa(unescape(encodeURIComponent(JSON.stringify(payload))));

const buildOfferLink = (offer) =>
  `${ECOM_URL}/quick-register?ref=${encodeURIComponent(encodeRef({ oc: offer.code }))}`;

const RULE_TYPES = [
  { value: 'first_order',      label: 'First Order' },
  { value: 'nth_order',        label: 'Nth Order' },
  { value: 'min_order_value',  label: 'Min Order Value' },
  { value: 'day_of_week',      label: 'Day of Week' },
  { value: 'promo_code',       label: 'Promo Code' },
  { value: 'referral',         label: 'Referral Sign-up' },
  { value: 'cumulative_spend', label: 'Cumulative Spend (Last N Days)' },
];

const REWARD_TYPES = [
  { value: 'discount_pct',              label: 'Discount %' },
  { value: 'discount_flat',             label: 'Flat Discount ₹' },
  { value: 'discount_above_threshold',  label: 'Amount Above Min (₹)' },
  { value: 'wallet_credit',             label: 'Wallet Credit ₹ (fixed)' },
  { value: 'wallet_credit_pct',         label: 'Wallet Credit % of Order' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const EMPTY_FORM = {
  code: '',
  title: '',
  description: '',
  active: true,
  valid_from: '',
  valid_to: '',
  usage_limit: '',
  per_customer_limit: '1',
  min_order_value: '0',
  applies_to: 'all',
  rule_type: 'promo_code',
  rule_value: {},
  reward_type: 'discount_flat',
  reward_value: '',
  reward_cap: '',
  stackable: false,
  first_order_only: false,
  assigned_only: false,
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtReward(offer) {
  if (offer.reward_type === 'discount_pct')             return `${offer.reward_value}% off`;
  if (offer.reward_type === 'discount_flat')            return `₹${parseFloat(offer.reward_value).toFixed(2)} off`;
  if (offer.reward_type === 'wallet_credit')            return `₹${parseFloat(offer.reward_value).toFixed(2)} wallet`;
  if (offer.reward_type === 'wallet_credit_pct')        return `${parseFloat(offer.reward_value)}% of order → wallet`;
  if (offer.reward_type === 'discount_above_threshold') return `Amount above ₹${parseFloat(offer.min_order_value).toFixed(0)}, max ₹${parseFloat(offer.reward_value).toFixed(0)}`;
  return offer.reward_value;
}

function fmtRule(offer) {
  if (offer.rule_type === 'first_order')     return 'First order';
  if (offer.rule_type === 'referral')        return 'Referral';
  if (offer.rule_type === 'promo_code')      return `Code: ${offer.code || '—'}`;
  if (offer.rule_type === 'nth_order')       return `Order #${offer.rule_value?.n || '?'}`;
  if (offer.rule_type === 'min_order_value') return `Min ₹${offer.min_order_value}`;
  if (offer.rule_type === 'day_of_week')      return `Days: ${(offer.rule_value?.days || []).join(', ')}`;
  if (offer.rule_type === 'cumulative_spend') return `₹${offer.rule_value?.min_spend || 0}+ in last ${offer.rule_value?.days || 30}d`;
  return offer.rule_type;
}

const ACTIVATE_RULES = [
  { value: 'first_order_50_pct', label: 'First Order 50% Off' },
];

const ACTIVATE_COUNTS = [
  { value: '5',   label: '5' },
  { value: '10',  label: '10' },
  { value: '50',  label: '50' },
  { value: '100', label: '100' },
  { value: '250', label: '250' },
  { value: '500', label: '500' },
  { value: '',    label: 'All' },
];

function ActivateOfferTab({ auth }) {
  const [rule, setRule]         = useState('first_order_50_pct');
  const [templateKey, setTemplateKey] = useState('');
  const [count, setCount]       = useState('50');
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);
  const [err, setErr]           = useState('');
  const [tplLov, setTplLov]     = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/whatsapp-templates`, auth())
      .then(r => {
        const active = (r.data || []).filter(t => t.is_active);
        setTplLov(active);
        if (active.length > 0 && !templateKey) setTemplateKey(active[0].template_key);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const handleRun = async () => {
    if (!templateKey) { setErr('WhatsApp template is required.'); return; }
    setRunning(true); setErr(''); setResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/offers/activate-offer`,
        { rule, template_key: templateKey, count: count || null },
        auth(),
      );
      setResult(data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to activate offer.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="activate-offer-panel">
      <div className="activate-offer-form">
        <div className="ao-field">
          <label>Rule</label>
          <select value={rule} onChange={e => setRule(e.target.value)}>
            {ACTIVATE_RULES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="ao-field ao-field--wide">
          <label>WhatsApp Template</label>
          <select value={templateKey} onChange={e => setTemplateKey(e.target.value)}>
            <option value="">— select template —</option>
            {tplLov.map(t => (
              <option key={t.template_key} value={t.template_key}>
                {t.template_key}{t.description ? ` — ${t.description}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="ao-field">
          <label>Number of Customers</label>
          <select value={count} onChange={e => setCount(e.target.value)}>
            {ACTIVATE_COUNTS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <button className="ao-run-btn" onClick={handleRun} disabled={running}>
          {running ? 'Running…' : 'Activate'}
        </button>
      </div>

      {err && <div className="ao-error">{err}</div>}

      {result && (
        <div className="ao-result">
          <div className="ao-result-stats">
            <div className="ao-stat"><span className="ao-stat-val">{result.activated}</span><span className="ao-stat-lbl">Activated</span></div>
            <div className="ao-stat"><span className="ao-stat-val ao-stat-val--ok">{result.whatsapp_sent}</span><span className="ao-stat-lbl">WhatsApp Sent</span></div>
            <div className="ao-stat"><span className="ao-stat-val ao-stat-val--err">{result.whatsapp_failed}</span><span className="ao-stat-lbl">WhatsApp Failed</span></div>
          </div>

          {result.customers && result.customers.length > 0 && (
            <div className="ao-customers-wrap">
              <table className="ao-customers-table">
                <thead>
                  <tr><th>#</th><th>Name</th><th>Phone</th></tr>
                </thead>
                <tbody>
                  {result.customers.map((c, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{c.name || '—'}</td>
                      <td>{c.phone || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Offers() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [activeTab, setActiveTab] = useState('offers');

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [editLocked, setEditLocked] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fallbackCopy = (text, id) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    try { document.execCommand('copy'); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); } catch {}
    document.body.removeChild(ta);
  };

  const handleCopyLink = (offer) => {
    const link = buildOfferLink(offer);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => { setCopiedId(offer.id); setTimeout(() => setCopiedId(null), 2000); })
        .catch(() => fallbackCopy(link, offer.id));
    } else {
      fallbackCopy(link, offer.id);
    }
  };

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/offers/admin`, auth());
      setOffers(data);
    } catch (err) {
      setError('Failed to load offers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, []); // eslint-disable-line

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setSaveError('');
    setShowModal(true);
  };

  const openEdit = (offer) => {
    setEditId(offer.id);
    setEditLocked(parseInt(offer.active_assignments || 0, 10) > 0);
    setForm({
      code:               offer.code         || '',
      title:              offer.title         || '',
      description:        offer.description  || '',
      active:             offer.active,
      valid_from:         offer.valid_from    ? offer.valid_from.split('T')[0]  : '',
      valid_to:           offer.valid_to      ? offer.valid_to.split('T')[0]    : '',
      usage_limit:        offer.usage_limit   != null ? String(offer.usage_limit)   : '',
      per_customer_limit: offer.per_customer_limit != null ? String(offer.per_customer_limit) : '1',
      min_order_value:    offer.min_order_value != null ? String(offer.min_order_value) : '0',
      applies_to:         offer.applies_to   || 'all',
      rule_type:          offer.rule_type,
      rule_value:         typeof offer.rule_value === 'string' ? JSON.parse(offer.rule_value) : (offer.rule_value || {}),
      reward_type:        offer.reward_type,
      reward_value:       String(offer.reward_value),
      reward_cap:         offer.reward_cap   != null ? String(offer.reward_cap)  : '',
      stackable:          offer.stackable,
      first_order_only:   offer.first_order_only,
      assigned_only:      offer.assigned_only || false,
    });
    setSaveError('');
    setShowModal(true);
  };

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleDay = (day) => {
    const days = form.rule_value?.days || [];
    const next = days.includes(day) ? days.filter(d => d !== day) : [...days, day];
    setF('rule_value', { ...form.rule_value, days: next });
  };

  const handleSave = async () => {
    if (!form.title.trim())    { setSaveError('Title is required.'); return; }
    if (!form.reward_value)    { setSaveError('Reward value is required.'); return; }
    if (!form.rule_type)       { setSaveError('Rule type is required.'); return; }

    setSaving(true); setSaveError('');
    try {
      const payload = {
        ...form,
        code:               form.code?.toUpperCase() || null,
        usage_limit:        form.usage_limit        ? parseInt(form.usage_limit, 10)        : null,
        per_customer_limit: form.per_customer_limit ? parseInt(form.per_customer_limit, 10) : 1,
        min_order_value:    parseFloat(form.min_order_value || 0),
        reward_value:       parseFloat(form.reward_value),
        reward_cap:         form.reward_cap         ? parseFloat(form.reward_cap)           : null,
        valid_from:         form.valid_from || null,
        valid_to:           form.valid_to   || null,
        assigned_only:      form.assigned_only,
      };

      if (editId) {
        await axios.put(`${API_URL}/offers/admin/${editId}`, payload, auth());
      } else {
        await axios.post(`${API_URL}/offers/admin`, payload, auth());
      }
      setShowModal(false);
      fetchOffers();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save offer.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this offer?')) return;
    try {
      await axios.delete(`${API_URL}/offers/admin/${id}`, auth());
      fetchOffers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete offer.');
    }
  };

  const handleToggleActive = async (offer) => {
    try {
      await axios.put(`${API_URL}/offers/admin/${offer.id}`, { ...offer, active: !offer.active }, auth());
      fetchOffers();
    } catch (err) {
      alert('Failed to update offer.');
    }
  };

  const renderOffersTab = () => {
    if (loading) return <div className="offers-loading">Loading...</div>;
    return (
      <div className="offers-table-wrap">
        <table className="offers-table">
          <thead>
            <tr>
              <th>Title / Code</th>
              <th>Rule</th>
              <th>Reward</th>
              <th>Valid</th>
              <th>Limits</th>
              <th>Redemptions</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {offers.length === 0 && (
              <tr><td colSpan={8} className="offers-empty">No offers yet. Create one to get started.</td></tr>
            )}
            {offers.map(offer => {
              const activeAssignments = parseInt(offer.active_assignments || 0, 10);
              const isLocked = activeAssignments > 0;
              const lockTitle = `${activeAssignments} customer(s) currently have this offer assigned. Editable once all are used.`;
              return (
                <tr key={offer.id}>
                  <td>
                    <div className="offer-title">{offer.title}</div>
                    <div className="offer-badges">
                      {offer.code && <span className="offer-code-badge">{offer.code}</span>}
                      {offer.assigned_only && <span className="offer-assigned-badge">Customer-assigned</span>}
                      {isLocked && <span className="offer-locked-badge" title={lockTitle}>🔒 {activeAssignments} assigned</span>}
                    </div>
                  </td>
                  <td>{fmtRule(offer)}</td>
                  <td>{fmtReward(offer)}</td>
                  <td className="offers-dates">
                    <span>{offer.valid_from ? fmtDate(offer.valid_from) : '—'}</span>
                    <span className="date-sep">to</span>
                    <span>{offer.valid_to ? fmtDate(offer.valid_to) : 'No expiry'}</span>
                  </td>
                  <td>
                    <div className="offer-limits">
                      {offer.usage_limit       != null && <span>Total: {offer.usage_limit}</span>}
                      {offer.per_customer_limit != null && <span>Per customer: {offer.per_customer_limit}</span>}
                    </div>
                  </td>
                  <td className="offer-redemption-count">{offer.redemption_count || 0}</td>
                  <td>
                    <button
                      className={`offer-status-btn ${offer.active ? 'active' : 'inactive'}`}
                      onClick={() => !isLocked && handleToggleActive(offer)}
                      disabled={isLocked}
                      title={isLocked ? lockTitle : undefined}
                    >
                      {offer.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <div className="offer-actions">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => openEdit(offer)}
                        title="Edit"
                      >✏️</button>
                      {offer.assigned_only && offer.code && (
                        <button
                          className={`btn-icon btn-copy-link${copiedId === offer.id ? ' btn-copy-link--copied' : ''}`}
                          onClick={() => handleCopyLink(offer)}
                          title={`Copy registration link: ${buildOfferLink(offer)}`}
                        >
                          {copiedId === offer.id ? '✓' : '🔗'}
                        </button>
                      )}
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(offer.id)}
                        disabled={isLocked}
                        title={isLocked ? lockTitle : 'Delete'}
                      >🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="offers-management">
      <div className="offers-header">
        <div>
          <h2>Offers</h2>
          <p>Manage promotional offers and promo codes for customers</p>
        </div>
        {activeTab === 'offers' && (
          <button className="btn-create" onClick={openCreate}>+ New Offer</button>
        )}
      </div>

      <div className="offers-tabs">
        <button
          className={`offers-tab${activeTab === 'offers' ? ' offers-tab--active' : ''}`}
          onClick={() => setActiveTab('offers')}
        >Offers</button>
        <button
          className={`offers-tab${activeTab === 'activate' ? ' offers-tab--active' : ''}`}
          onClick={() => setActiveTab('activate')}
        >Activate Offer</button>
      </div>

      {activeTab === 'activate' && <ActivateOfferTab auth={auth} />}

      {activeTab === 'offers' && error && <div className="offers-error">{error}</div>}

      {activeTab === 'offers' && renderOffersTab()}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="offers-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="offers-modal" onClick={e => e.stopPropagation()}>
            <div className="offers-modal-header">
              <h3>{editId ? 'Edit Offer' : 'New Offer'}</h3>
              <button className="offers-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="offers-modal-body">
              {/* Basic info */}
              <div className="offers-form-section">
                <div className="offers-form-row">
                  <div className="offers-field offers-field--wide">
                    <label>Title <span className="req">*</span></label>
                    <input value={form.title} onChange={e => setF('title', e.target.value)} placeholder="e.g. First Order Discount" />
                  </div>
                  <div className="offers-field">
                    <label>Promo Code</label>
                    <input value={form.code} onChange={e => setF('code', e.target.value.toUpperCase())} placeholder="SAVE10" />
                  </div>
                  <div className="offers-field offers-field--check">
                    <label>Active</label>
                    <input type="checkbox" checked={form.active} onChange={e => setF('active', e.target.checked)} />
                  </div>
                  <div className="offers-field offers-field--check">
                    <label title={editLocked ? 'Cannot change while offer is assigned to customers' : 'If checked, this offer is only applied when directly assigned to a customer. It will never auto-apply via rules.'}>
                      Customer-assigned only
                    </label>
                    <input
                      type="checkbox"
                      checked={form.assigned_only}
                      onChange={e => setF('assigned_only', e.target.checked)}
                      disabled={editLocked}
                      style={editLocked ? { cursor: 'not-allowed', opacity: 0.45 } : undefined}
                    />
                  </div>
                </div>
                <div className="offers-field offers-field--full">
                  <label>Description</label>
                  <textarea rows={2} value={form.description} onChange={e => setF('description', e.target.value)} placeholder="Short description shown to customers" />
                </div>
              </div>

              {/* Validity */}
              <div className="offers-form-section">
                <div className="offers-section-title">Validity</div>
                <div className="offers-form-row">
                  <div className="offers-field">
                    <label>Valid From</label>
                    <input type="date" value={form.valid_from} onChange={e => setF('valid_from', e.target.value)} />
                  </div>
                  <div className="offers-field">
                    <label>Valid To</label>
                    <input type="date" value={form.valid_to} onChange={e => setF('valid_to', e.target.value)} />
                  </div>
                  <div className="offers-field">
                    <label>Total Usage Limit</label>
                    <input type="number" min="1" value={form.usage_limit} onChange={e => setF('usage_limit', e.target.value)} placeholder="Unlimited" />
                  </div>
                  <div className="offers-field">
                    <label>Per-Customer Limit</label>
                    <input type="number" min="1" value={form.per_customer_limit} onChange={e => setF('per_customer_limit', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Rule */}
              <div className="offers-form-section">
                <div className="offers-section-title">Eligibility Rule</div>
                <div className="offers-form-row">
                  <div className="offers-field">
                    <label>Rule Type <span className="req">*</span></label>
                    <select value={form.rule_type} onChange={e => setF('rule_type', e.target.value)}>
                      {RULE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div className="offers-field">
                    <label>Min Order Value (₹)</label>
                    <input type="number" min="0" step="0.01" value={form.min_order_value} onChange={e => setF('min_order_value', e.target.value)} />
                  </div>
                </div>

                {form.rule_type === 'nth_order' && (
                  <div className="offers-form-row">
                    <div className="offers-field">
                      <label>Trigger on Order # (N)</label>
                      <input type="number" min="2" value={form.rule_value?.n || ''} onChange={e => setF('rule_value', { ...form.rule_value, n: e.target.value })} placeholder="e.g. 5 for every 5th order" />
                    </div>
                  </div>
                )}

                {form.rule_type === 'day_of_week' && (
                  <div className="offers-field">
                    <label>Active Days</label>
                    <div className="offers-day-toggles">
                      {DAYS.map(d => (
                        <button key={d} type="button"
                          className={`offers-day-btn ${(form.rule_value?.days || []).includes(d) ? 'selected' : ''}`}
                          onClick={() => toggleDay(d)}
                        >{d}</button>
                      ))}
                    </div>
                  </div>
                )}

                {form.rule_type === 'cumulative_spend' && (
                  <div className="offers-form-row">
                    <div className="offers-field">
                      <label>Lookback Window (days) <span className="req">*</span></label>
                      <input
                        type="number" min="1"
                        value={form.rule_value?.days || ''}
                        onChange={e => setF('rule_value', { ...form.rule_value, days: parseInt(e.target.value, 10) || '' })}
                        placeholder="e.g. 30"
                      />
                    </div>
                    <div className="offers-field">
                      <label>Min Cumulative Spend (₹) <span className="req">*</span></label>
                      <input
                        type="number" min="0" step="0.01"
                        value={form.rule_value?.min_spend || ''}
                        onChange={e => setF('rule_value', { ...form.rule_value, min_spend: parseFloat(e.target.value) || '' })}
                        placeholder="e.g. 5000"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Reward */}
              <div className="offers-form-section">
                <div className="offers-section-title">Reward</div>
                <div className="offers-form-row">
                  <div className="offers-field">
                    <label>Reward Type <span className="req">*</span></label>
                    <select value={form.reward_type} onChange={e => setF('reward_type', e.target.value)}>
                      {REWARD_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div className="offers-field">
                    <label>
                      {form.reward_type === 'discount_pct'             ? 'Discount %' :
                       form.reward_type === 'wallet_credit'            ? 'Credit Amount (₹)' :
                       form.reward_type === 'wallet_credit_pct'        ? 'Credit % of Order' :
                       form.reward_type === 'discount_above_threshold' ? 'Max Discount Cap (₹)' :
                       'Discount Amount (₹)'}
                      <span className="req"> *</span>
                    </label>
                    <input type="number" min="0" step="0.01" value={form.reward_value} onChange={e => setF('reward_value', e.target.value)} placeholder="0" />
                  </div>
                  {form.reward_type === 'discount_pct' && (
                    <div className="offers-field">
                      <label>Max Discount Cap (₹)</label>
                      <input type="number" min="0" step="0.01" value={form.reward_cap} onChange={e => setF('reward_cap', e.target.value)} placeholder="No cap" />
                    </div>
                  )}
                  <div className="offers-field offers-field--check">
                    <label>Stackable</label>
                    <input type="checkbox" checked={form.stackable} onChange={e => setF('stackable', e.target.checked)} />
                  </div>
                  <div className="offers-field offers-field--check">
                    <label>First Order Only</label>
                    <input type="checkbox" checked={form.first_order_only} onChange={e => setF('first_order_only', e.target.checked)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="offers-modal-footer">
              {saveError && <span className="offers-save-error">{saveError}</span>}
              <button className="btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="btn-submit" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Save Changes' : 'Create Offer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
